import React from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Layers, Grid3x3, Upload, Sparkles, Download, Share2, Flame, CalendarClock, Crown, FolderOpen, Info, BookOpen, Rocket } from "lucide-react";

const FEATURES = [
  { icon: Grid3x3, title: "Recursive 3×3 grids", desc: "One center topic + 8 AI sub-topics, each with an icon, description, and flashcard. Drill down up to 6 layers." },
  { icon: Upload, title: "Type or upload notes", desc: "Start from a typed subject or upload PDF / DOCX / TXT / image notes — cells are grounded in your actual notes." },
  { icon: Sparkles, title: "Study viewer", desc: "Flip-card review with Again/Hard/Good/Easy. Toggle Whole-Branch to study a grid plus all its sub-grids." },
  { icon: CalendarClock, title: "Spaced review", desc: "Ratings feed an SM-2 schedule. The Review (N) button surfaces exactly the cards due today." },
  { icon: Flame, title: "Progress streaks", desc: "A daily study streak and total-reviewed counter keep you consistent." },
  { icon: Download, title: "Real Anki export", desc: "Download a genuine .apkg (current layer or master tree) with :: hierarchical sub-decks — built in your browser." },
  { icon: FolderOpen, title: "Auto-saved maps", desc: "Every explored tree auto-saves to My Maps and reopens exactly where you left off." },
  { icon: Share2, title: "Shareable maps", desc: "Share a read-only link with a rich social preview so classmates can browse your map." },
  { icon: Crown, title: "Free & Premium", desc: "Free: 3 maps + 5 uploads + 30 generations/mo. Premium: 100 generations/mo, unlimited uploads, viewer & master export." },
];

const STEPS = [
  "Start a map — type a subject and hit Explore, or upload your class notes.",
  "Explore — click any of the 8 outer cells to dive deeper; use the breadcrumb to jump back up.",
  "Study — open Study This Grid to flip cards; toggle Whole Branch to review everything beneath your spot.",
  "Keep your streak — rate cards, then come back and hit Review (N) to clear cards that are due.",
  "Export to Anki — choose Current Layer or Master Tree and import the .apkg into Anki.",
  "Save & share — maps auto-save to My Maps; hit Share to copy a read-only link.",
];

export default function HelpDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="help-dialog" className="max-w-lg bg-[#0D0B18] border border-purple-500/25 text-white max-h-[85vh] overflow-hidden flex flex-col">
        <DialogTitle className="sr-only">About and Help</DialogTitle>
        <DialogDescription className="sr-only">Learn what FractalNotes does and how to use it.</DialogDescription>

        <div className="flex items-center gap-2">
          <div className="p-2 rounded-xl bg-purple-600/20 border border-purple-500/30"><Layers className="w-5 h-5 text-purple-300" /></div>
          <div>
            <h3 className="font-display text-xl font-bold text-white leading-tight">FractalNotes AI</h3>
            <p className="text-xs text-purple-300/60">See the big picture by mastering the tiny details.</p>
          </div>
        </div>

        <Tabs defaultValue="about" className="mt-2 flex-1 min-h-0 flex flex-col">
          <TabsList className="bg-[#07070E] border border-purple-500/20 rounded-full p-1 self-start">
            <TabsTrigger data-testid="help-tab-about" value="about" className="rounded-full data-[state=active]:bg-purple-600 data-[state=active]:text-white text-purple-200 px-4 text-xs">
              <Info className="w-3.5 h-3.5 mr-1.5" /> About
            </TabsTrigger>
            <TabsTrigger data-testid="help-tab-features" value="features" className="rounded-full data-[state=active]:bg-purple-600 data-[state=active]:text-white text-purple-200 px-4 text-xs">
              <BookOpen className="w-3.5 h-3.5 mr-1.5" /> Features
            </TabsTrigger>
            <TabsTrigger data-testid="help-tab-howto" value="howto" className="rounded-full data-[state=active]:bg-purple-600 data-[state=active]:text-white text-purple-200 px-4 text-xs">
              <Rocket className="w-3.5 h-3.5 mr-1.5" /> How to use
            </TabsTrigger>
          </TabsList>

          <div className="mt-4 overflow-y-auto pr-1 flex-1 min-h-0">
            <TabsContent value="about" className="mt-0 space-y-3">
              <p className="text-sm text-purple-100 leading-relaxed">
                FractalNotes turns any subject — or your own class notes — into an interactive, infinitely-zoomable 3×3 concept map.
              </p>
              <p className="text-sm text-purple-200/80 leading-relaxed">
                Start with one topic in the center of a grid. AI surrounds it with the 8 most important sub-topics, each with an icon,
                a one-line explanation, and a built-in flashcard. Click any sub-topic and it becomes the center of a brand-new grid with
                8 deeper concepts — up to 6 layers deep. As you explore, a study deck builds itself for in-app review or Anki export.
              </p>
              <div className="rounded-xl bg-purple-500/8 border border-purple-500/20 p-3 text-xs text-purple-200/80">
                <span className="text-purple-300 font-semibold">Powered by</span> Anthropic Claude (Sonnet for root breakdowns,
                Haiku for fast deeper branches). Built for students and self-learners who think better in maps than in walls of text.
              </div>
            </TabsContent>

            <TabsContent value="features" className="mt-0 space-y-2.5">
              {FEATURES.map((f) => (
                <div key={f.title} className="flex gap-3 rounded-xl bg-purple-500/5 border border-purple-500/15 p-3">
                  <div className="p-1.5 rounded-lg bg-purple-500/15 border border-purple-500/25 h-fit">
                    <f.icon className="w-4 h-4 text-purple-300" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{f.title}</p>
                    <p className="text-xs text-purple-200/70 leading-snug">{f.desc}</p>
                  </div>
                </div>
              ))}
            </TabsContent>

            <TabsContent value="howto" className="mt-0 space-y-2.5">
              {STEPS.map((s, idx) => (
                <div key={idx} className="flex gap-3 items-start">
                  <span className="shrink-0 w-6 h-6 rounded-full bg-purple-600/25 border border-purple-500/40 text-purple-100 text-xs font-bold flex items-center justify-center">
                    {idx + 1}
                  </span>
                  <p className="text-sm text-purple-100 leading-snug pt-0.5">{s}</p>
                </div>
              ))}
              <div className="rounded-xl bg-amber-500/10 border border-amber-400/20 p-3 text-xs text-amber-200/90 mt-2">
                💡 Tip: Premium unlocks the flashcard viewer, whole-branch study, unlimited uploads, and master Anki export.
                Payments are in test mode — use card 4242 4242 4242 4242.
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
